import { enableField, disableField, tritonGainControlChange } from '../../../actions';
import { RowItemType, Section, SelectorIdentifier, UIElementType } from '../../../models';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { Mp1SensorIds, Mp1SensorTitles, Mp2SensorIds, Mp2SensorTitles, TritonSensors, getSensorFieldId } from '../base-config-models';
import { LongRangeSensorFieldIds } from './config-models';
import { MonitoringPointElement } from 'app/shared/components/location-card/models/location-card.model';
import { GAIN_CONTROL_AUTO_NUMERIC, GainControlValue } from 'app/shared/components/location-card/location-card.constants';
import { LocationCardSettings } from '../../../reducers';
import { field } from '../../../core/field-schema';
import { required, pattern, min, max } from '../../../core/validator-builders';
import { LongRangeDevice } from '../models';

/**
 * Long Range Depth Triton sensor configuration
 */
export const longRangeDepthSensor = (mp: 0 | 1, settings: LocationCardSettings, sensorData: LongRangeDevice): Section => {
    const isMetric = settings.isMetric;

    return {
        info: {
            id: mp === 0 ? Mp1SensorIds[TritonSensors.LongRange] : Mp2SensorIds[TritonSensors.LongRange],
            title: mp === 0 ? Mp1SensorTitles[TritonSensors.LongRange] : Mp2SensorTitles[TritonSensors.LongRange],
            expandable: true,
            displayTitle: true,
            expanded: false,
        },
        rows: [
            // Physical Offset & Store Data Row
            {
                info: {
                    id: 'physical-offset-store-data-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.number(getSensorFieldId(LongRangeSensorFieldIds.PhysicalOffset, mp), 'physicalOffset', {
                        placeholder: 'ADD_EDIT.PHYSICAL_OFFSET',
                        placeholderSuffix: settings.depthUnit,
                        value: sensorData ? sensorData.physicalOffset : isMetric ? 355.6 : 14,
                        validators: [
                            required(),
                            pattern({
                                value: isMetric
                                    ? REGEX_CONFIG.negativeWithOneDecimalPattern
                                    : REGEX_CONFIG.negativeWithTwoDecimalPattern,
                                message: isMetric ? 'COMMON.OFFSET_METRIC_ERROR' : 'COMMON.OFFSET_NON_METRIC_ERROR',
                            }),
                        ],
                        panelClass: 'each-Item',
                    }),
                    field.toggle(getSensorFieldId(LongRangeSensorFieldIds.StoreData, mp), 'storeData', {
                        value: sensorData ? sensorData.storeData : true,
                        label: 'ADD_EDIT.STORE_DATA',
                        panelClass: 'each-Item item-flex-center',
                    }),
                ],
            },

            // Blanking & Temperature Sensor Row
            {
                info: {
                    id: 'blanking-temperature-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.number(getSensorFieldId(LongRangeSensorFieldIds.Blanking, mp), 'blanking', {
                        placeholder: 'ADD_EDIT.BLANKING',
                        placeholderSuffix: settings.depthUnit,
                        value: sensorData ? sensorData.blanking : 0,
                        validators: [
                            required(),
                            pattern({
                                value: isMetric
                                    ? REGEX_CONFIG.numericDecimal1PlacePattern
                                    : REGEX_CONFIG.numericDecimal2PlacePattern,
                                message: isMetric ? 'COMMON.OFFSET_METRIC_ERROR' : 'COMMON.OFFSET_NON_METRIC_ERROR',
                            }),
                        ],
                        panelClass: 'each-Item required',
                    }),
                    field.select<MonitoringPointElement, number>(getSensorFieldId(LongRangeSensorFieldIds.TemperatureSensor, mp), 'temperatureSensor', {
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        placeholder: 'LOCATION_DASHBOARD.TEMPERATURE_SCALE_AXIS',
                        value: sensorData ? sensorData.temperatureSensor : 0,
                        optionsSelector: SelectorIdentifier.SensorTemp,
                        panelClass: 'each-Item',
                    }),
                ],
            },

            // Power & Electronic Offset Row
            {
                info: {
                    id: 'power-electronic-offset-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<MonitoringPointElement, number>(getSensorFieldId(LongRangeSensorFieldIds.Power, mp), 'power', {
                        placeholder: 'ADD_EDIT.POWER',
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        value: sensorData ? sensorData.power : 0,
                        optionsSelector: SelectorIdentifier.SensorPower,
                        panelClass: 'each-Item',
                    }),
                    field.number(getSensorFieldId(LongRangeSensorFieldIds.ElectronicOffset, mp), 'electronicOffset', {
                        placeholder: 'ADD_EDIT.ELECTRONIC_OFFSET',
                        placeholderSuffix: settings.depthUnit,
                        value: sensorData ? sensorData.electronicOffset : 0,
                        validators: [
                            required(),
                            pattern({
                                value: isMetric
                                    ? REGEX_CONFIG.negativeWithOneDecimalPattern
                                    : REGEX_CONFIG.negativeWithTwoDecimalPattern,
                                message: isMetric ? 'COMMON.OFFSET_METRIC_ERROR' : 'COMMON.OFFSET_NON_METRIC_ERROR',
                            }),
                        ],
                        panelClass: 'each-Item required',
                    }),
                ],
            },

            // Gain Control Row
            {
                info: {
                    id: 'gain-control-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<MonitoringPointElement, string>(getSensorFieldId(LongRangeSensorFieldIds.GainControlSelection, mp), 'gainControlSelection', {
                        placeholder: 'ADD_EDIT.GAIN_CONTROL',
                        value: sensorData && sensorData.gainControl !== null && sensorData.gainControl !== GAIN_CONTROL_AUTO_NUMERIC
                            ? GainControlValue.Manual
                            : GainControlValue.Auto,
                        valueMapper: (option) => option.text,
                        displayMapper: (option) => option.text,
                        optionsSelector: SelectorIdentifier.GainControlOptions,
                        panelClass: 'each-Item',
                        events: {
                            onChange: (value) => [
                                tritonGainControlChange({ isManual: value.text === GainControlValue.Manual, sensorIndex: mp }),
                            ],
                        },
                    }),
                    field.select<MonitoringPointElement, number>(getSensorFieldId(LongRangeSensorFieldIds.GainControl, mp), 'gainControl', {
                        placeholder: 'ADD_EDIT.GAIN_VALUE',
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        value: sensorData ? sensorData.gainControl : null,
                        disabled: !(sensorData && sensorData.gainControl !== null && sensorData.gainControl !== GAIN_CONTROL_AUTO_NUMERIC),
                        optionsSelector: SelectorIdentifier.GainValues,
                        panelClass: 'each-Item',
                        visible: !!(sensorData && sensorData.gainControl !== null && sensorData.gainControl !== GAIN_CONTROL_AUTO_NUMERIC),
                    }),
                ],
            },

            // Compensated Temperature Title Row
            {
                info: {
                    id: 'compensated-temp-title-row',
                    panelClass: 'full-width',
                },
                items: [
                    {
                        id: 'compensated-temp-title',
                        type: RowItemType.UI,
                        uiType: UIElementType.Text,
                        name: 'compensatedTempTitle',
                        label: 'ADD_EDIT.COMPENSATED_TEMPERATURE',
                        panelClass: 'input-text font-600',
                    },
                ],
            },

            // Compensated Temperature Values Row
            {
                info: {
                    id: 'compensated-temp-values-row',
                    panelClass: 'custom-50p text-space pad-t-15',
                },
                items: [
                    field.number(getSensorFieldId(LongRangeSensorFieldIds.CompensatedTemperatureMarch, mp), 'compensatedTemperatureMarch', {
                        placeholder: 'ADD_EDIT.MARCH',
                        placeholderSuffix: settings.tempUnit,
                        value: sensorData
                            ? Number(sensorData.compensatedTemperatureMarch.toFixed(2))
                            : isMetric
                            ? 12.78
                            : 55,
                        validators: [
                            required(),
                            pattern({
                                value: isMetric
                                    ? REGEX_CONFIG.numericDecimal2PlacePattern
                                    : REGEX_CONFIG.numericDecimal1PlacePattern,
                                message: 'COMMON.TEMPERATURE_ERROR',
                            }),
                        ],
                        panelClass: 'each-Item required',
                    }),
                    field.number(getSensorFieldId(LongRangeSensorFieldIds.CompensatedTemperatureSeptember, mp), 'compensatedTemperatureSeptember', {
                        placeholder: 'ADD_EDIT.SEPTEMBER',
                        placeholderSuffix: settings.tempUnit,
                        value: sensorData
                            ? Number(sensorData.compensatedTemperatureSeptember.toFixed(2))
                            : isMetric
                            ? 22.78
                            : 73,
                        validators: [
                            required(),
                            pattern({
                                value: isMetric
                                    ? REGEX_CONFIG.numericDecimal2PlacePattern
                                    : REGEX_CONFIG.numericDecimal1PlacePattern,
                                message: 'COMMON.TEMPERATURE_ERROR',
                            }),
                        ],
                        panelClass: 'each-Item required',
                    }),
                ],
            },

            // Submersion Sensor Toggle Row
            {
                info: {
                    id: 'submersion-sensor-row',
                    panelClass: 'full-width pad-b-15 input-text',
                },
                items: [
                    field.toggle(getSensorFieldId(LongRangeSensorFieldIds.DisableCapacitance, mp), 'disableCapacitance', {
                        value: sensorData ? sensorData.disableCapacitance : true,
                        label: 'ADD_EDIT.SUBMERSION_SENSOR',
                        panelClass: 'toggle toggle-text highlight-text item-flex-center',
                        events: {
                            onChange: (value) => [
                                value
                                    ? enableField({ fieldId: getSensorFieldId(LongRangeSensorFieldIds.LogCapacitance, mp) })
                                    : disableField({ fieldId: getSensorFieldId(LongRangeSensorFieldIds.LogCapacitance, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(LongRangeSensorFieldIds.SurchargeThreshold, mp) })
                                    : disableField({ fieldId: getSensorFieldId(LongRangeSensorFieldIds.SurchargeThreshold, mp) }),
                            ],
                        },
                    }),
                ],
            },

            // Logging & Threshold Row
            {
                info: {
                    id: 'logging-threshold-row',
                    panelClass: 'custom-50p text-space',
                },
                items: [
                    field.toggle(getSensorFieldId(LongRangeSensorFieldIds.LogCapacitance, mp), 'logCapacitance', {
                        value: sensorData ? sensorData.logCapacitance : false,
                        disabled: sensorData ? !sensorData.disableCapacitance : false,
                        label: 'ADD_EDIT.LOGGING',
                        panelClass: 'each-Item auto-margin-t-b item-flex-center',
                    }),
                    field.number(getSensorFieldId(LongRangeSensorFieldIds.SurchargeThreshold, mp), 'surchargeThreshold', {
                        placeholder: 'ADD_EDIT.THRESHOLD',
                        disabled: sensorData ? !sensorData.disableCapacitance : false,
                        value: sensorData ? sensorData.surchargeThreshold : 85,
                        validators: [required(), min({ value: 70 }), max({ value: 99 })],
                        panelClass: 'each-Item required',
                        events: {
                            // onChange: (value) => [setFieldValue({ fieldName: LongRangeSensorFieldIds.SurchargeThreshold, value })],
                        },
                    }),
                ],
            },
        ],
    };
};
